
import { useState, useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';

interface PathContent {
  path: string;
  title: string;
  subtitle: string;
}

const DEFAULT_PATH_CONTENTS: PathContent[] = [
  {
    path: '/',
    title: "Let's Make it Happen",
    subtitle: "Get Your Free Quote Today!"
  },
  {
    path: '/about-us/',
    title: "Let's Make it Happen,",
    subtitle: 'Get Your Free Quote Today!'
  },
  {
    path: '/90-day-mvp-development',
    title: "Ready to Launch Your MVP",
    subtitle: "Say HI!"
  },
  {
    path: '/ai-automation',
    title: 'Ready to ',
    subtitle: 'Automate Your Business Journey?'
  },
  {
    path: '/staff-augmentation',
    title: 'Ready to Hire an Elite Developer in',
    subtitle: "48 Hours?"
  },
  {
    path: '/build-a-website-for-my-business',
    title: 'Ready to Launch Your Lightning Site?',
    subtitle: "Say HI!"
  },
  {
    path: '/portfolio',
    title: "Let's Make it Happen",
    subtitle: "Get Your Free Quote Today!"
  }
];


interface UseScrollModalOptions {
  desktopThreshold: number;
  mobileThreshold: number;
  mobileBreakpoint?: number;
  showOnlyOnce?: boolean;
  pathContents?: PathContent[];
  defaultTitle?: string;
  defaultSubtitle?: string;
}

interface UseScrollModalReturn {
  isVisible: boolean;
  closeModal: () => void;
  title: string;
  subtitle: string;
}


function useScrollModal({
  desktopThreshold,
  mobileThreshold,
  mobileBreakpoint = 768,
  showOnlyOnce = true,
  pathContents = DEFAULT_PATH_CONTENTS,
  defaultTitle = "Let's Make It Happen",
  defaultSubtitle = "",
}: UseScrollModalOptions): UseScrollModalReturn {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [hasShown, setHasShown] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(defaultTitle);
  const [subtitle, setSubtitle] = useState<string>(defaultSubtitle);


  const { shownModals, setModalShown } = useAppStore();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentPath = window.location.pathname;


    if (currentPath === '/join-us/' || currentPath === '/join-us') {
      return;
    }

    const currentPathContent = pathContents.find(
      item => item.path === currentPath ||
      (currentPath.endsWith('/') ? item.path === currentPath.slice(0, -1) : item.path === `${currentPath}/`)
    );

    if (currentPathContent) {
      setTitle(currentPathContent.title);
      setSubtitle(currentPathContent.subtitle);
    } else {
      setTitle(defaultTitle);
      setSubtitle(defaultSubtitle);
    }

    // Check if this modal has been shown before using the App Store
    if (showOnlyOnce && shownModals[currentPath]) {
      setHasShown(true);

      return;
    }

    const handleScroll = () => {
      if (hasShown && showOnlyOnce) return;

      const isMobile = window.innerWidth <= mobileBreakpoint;
      const threshold = isMobile ? mobileThreshold : desktopThreshold;

      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrollPercentage = (scrollPosition / (documentHeight - windowHeight)) * 100;

      if (scrollPercentage >= threshold && !isVisible) {
        setIsVisible(true);
        setHasShown(true);

        if (showOnlyOnce) {
          setModalShown(currentPath);

        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [
    desktopThreshold,
    mobileThreshold,
    mobileBreakpoint,
    showOnlyOnce,
    isVisible,
    hasShown,
    pathContents,
    defaultTitle,
    defaultSubtitle,
    shownModals,
    setModalShown
  ]);

  const closeModal = () => {
    setIsVisible(false);
  };

  return { isVisible, closeModal, title, subtitle };
}

export default useScrollModal;
